/**
 * Transaction list with filters.
 *
 * Grouped by day. Reversed entries are hidden by default and can be shown —
 * they are history, not clutter, but they are not what you scan a list for.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, ListOrdered, Loader2, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label, Switch } from '@/components/ui/misc';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EmptyState, ErrorState, PageHeader, RowSkeleton } from '@/components/common';
import { TransactionRow } from '@/components/TransactionRow';
import { VoidDialog } from '@/components/VoidDialog';
import { QuickAddSheet } from '@/components/QuickAddSheet';
import { KIND_LABELS } from '@/components/Money';
import { formatDateRelative } from '@/lib/format';
import { useCategories, useCurrencies, useFunds, useLocations, useTransactions } from '@/hooks/useApi';
import { useDebounced } from '@/hooks/useDebounced';
import type { Transaction, TransactionKind } from '@/lib/types';

export function TransactionsPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [voidTarget, setVoidTarget] = useState<Transaction | null>(null);
  const [quickAdd, setQuickAdd] = useState(false);
  const [search, setSearch] = useState(params.get('q') ?? '');

  const debouncedSearch = useDebounced(search, 300);

  const { data: funds = [] } = useFunds();
  const { data: locations = [] } = useLocations();
  const { data: categories = [] } = useCategories();
  const { data: currencies = [] } = useCurrencies();

  const filters = useMemo(
    () => ({
      fundId: numeric(params.get('fundId')),
      locationId: numeric(params.get('locationId')),
      categoryId: numeric(params.get('categoryId')),
      kind: params.get('kind') ?? undefined,
      currency: params.get('currency') ?? undefined,
      from: params.get('from') ?? undefined,
      to: params.get('to') ?? undefined,
      includeVoided: params.get('includeVoided') === 'true',
      q: debouncedSearch || undefined,
    }),
    [params, debouncedSearch],
  );

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useTransactions(filters);

  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  /** Group by day so the eye has anchors instead of one undifferentiated list. */
  const grouped = useMemo(() => {
    const map = new Map<string, Transaction[]>();
    for (const tx of items) {
      const list = map.get(tx.date) ?? [];
      list.push(tx);
      map.set(tx.date, list);
    }
    return [...map.entries()];
  }, [items]);

  const setFilter = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value == null || value === '' || value === 'all') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const activeFilterCount = ['fundId', 'locationId', 'categoryId', 'kind', 'currency', 'from', 'to'].filter(
    (k) => params.get(k),
  ).length;

  return (
    <div>
      <PageHeader
        title="Операции"
        description={items.length > 0 ? `${items.length} заредени` : undefined}
        actions={
          <>
            <Button
              variant={activeFilterCount > 0 ? 'secondary' : 'outline'}
              size="sm"
              className="gap-2"
              onClick={() => setShowFilters((v) => !v)}
            >
              <Filter className="h-4 w-4" />
              Филтри
              {activeFilterCount > 0 ? (
                <span className="rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
                  {activeFilterCount}
                </span>
              ) : null}
            </Button>
            <Button size="sm" onClick={() => setQuickAdd(true)}>
              Нова
            </Button>
          </>
        }
      />

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Търси в описание, бележка, контрагент…"
          className="pl-9 pr-9"
        />
        {search ? (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Изчисти търсенето"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {showFilters ? (
        <Card className="mb-4">
          <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
            <FilterSelect
              label="Каса"
              value={params.get('fundId')}
              onChange={(v) => setFilter('fundId', v)}
              options={funds.map((f) => ({ value: String(f.id), label: f.name }))}
            />
            <FilterSelect
              label="Място"
              value={params.get('locationId')}
              onChange={(v) => setFilter('locationId', v)}
              options={locations.map((l) => ({ value: String(l.id), label: l.name }))}
            />
            <FilterSelect
              label="Вид"
              value={params.get('kind')}
              onChange={(v) => setFilter('kind', v)}
              options={(Object.keys(KIND_LABELS) as TransactionKind[]).map((k) => ({
                value: k,
                label: KIND_LABELS[k],
              }))}
            />
            <FilterSelect
              label="Категория"
              value={params.get('categoryId')}
              onChange={(v) => setFilter('categoryId', v)}
              options={categories.map((c) => ({ value: String(c.id), label: c.name }))}
            />
            <FilterSelect
              label="Валута"
              value={params.get('currency')}
              onChange={(v) => setFilter('currency', v)}
              options={currencies.map((c) => ({ value: c.code, label: `${c.symbol} ${c.code}` }))}
            />

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="f-from">От</Label>
                <Input
                  id="f-from"
                  type="date"
                  value={params.get('from') ?? ''}
                  onChange={(e) => setFilter('from', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="f-to">До</Label>
                <Input
                  id="f-to"
                  type="date"
                  value={params.get('to') ?? ''}
                  onChange={(e) => setFilter('to', e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 sm:col-span-2 lg:col-span-3">
              <div className="flex items-center gap-2">
                <Switch
                  id="f-voided"
                  checked={params.get('includeVoided') === 'true'}
                  onCheckedChange={(c) => setFilter('includeVoided', c ? 'true' : undefined)}
                />
                <Label htmlFor="f-voided">Покажи и сторнираните</Label>
              </div>
              {activeFilterCount > 0 ? (
                <Button variant="ghost" size="sm" onClick={() => setParams(new URLSearchParams())}>
                  Изчисти филтрите
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : isLoading ? (
        <RowSkeleton count={8} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={ListOrdered}
            title={activeFilterCount > 0 || search ? 'Нищо не съвпада' : 'Няма операции'}
            description={
              activeFilterCount > 0 || search
                ? 'Разхлаби филтрите или потърси друго.'
                : 'Добави първата операция и тя ще се появи тук.'
            }
            action={
              activeFilterCount > 0 || search ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setParams(new URLSearchParams());
                    setSearch('');
                  }}
                >
                  Изчисти
                </Button>
              ) : (
                <Button onClick={() => setQuickAdd(true)}>Добави операция</Button>
              )
            }
          />
        </Card>
      ) : (
        <div className="space-y-5">
          {grouped.map(([date, txs]) => (
            <section key={date}>
              <h2 className="mb-2 text-sm font-medium text-muted-foreground">
                {formatDateRelative(date)}
              </h2>
              <div className="space-y-2">
                {txs.map((tx) => (
                  <TransactionRow key={tx.id} tx={tx} onVoid={setVoidTarget} showDate={false} />
                ))}
              </div>
            </section>
          ))}

          {hasNextPage ? (
            <Button
              variant="outline"
              className="w-full"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Зареди още
            </Button>
          ) : null}
        </div>
      )}

      <VoidDialog tx={voidTarget} onClose={() => setVoidTarget(null)} />
      <QuickAddSheet open={quickAdd} onOpenChange={setQuickAdd} />
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string | null;
  onChange: (value: string | undefined) => void;
  options: Array<{ value: string; label: string }>;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value ?? 'all'} onValueChange={(v) => onChange(v === 'all' ? undefined : v)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Всички</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function numeric(v: string | null): number | undefined {
  if (!v) return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}
